import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog, statusInfo, toast } from '@elbklar/ui';
import { customersApi, type RueckwegVorschau } from '@/lib/api';

interface ZurueckZumLeadDialogProps {
  customerId: string;
  /** Für die Anrede im Dialog und die Erfolgsmeldung. */
  companyName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type RueckvorschauOderNichts = RueckwegVorschau | undefined;

/**
 * „3 Anfragen", „1 Anfrage", „keine Anfrage". Die Nullform steht dabei, weil
 * das Geschlecht sie bestimmt: keine Anfrage, kein Projekt, kein Angebot.
 */
function zahlwort(n: number, einzahl: string, mehrzahl: string, keins: string): string {
  if (n === 0) return keins;
  if (n === 1) return `1 ${einzahl}`;
  return `${n} ${mehrzahl}`;
}

/** „A", „A und B", „A, B und C". */
function aufzaehlung(teile: string[]): string {
  if (teile.length <= 1) return teile[0] ?? '';
  return `${teile.slice(0, -1).join(', ')} und ${teile[teile.length - 1]}`;
}

/**
 * Was in der Rückfrage steht. Die Zahlen kommen aus
 * `GET /customers/{id}/revert-to-lead/preview`, damit der Mensch die Folgen
 * sieht, statt vor einer Sperre zu stehen: Am 29.10.2026 sind zwischen 08:42
 * und 09:22 neun Kundensätze gelöscht worden, weil es keinen Rückweg gab.
 */
function rueckfrageZeilen(v: RueckvorschauOderNichts, companyName: string) {
  const ziel = v ? statusInfo(v.ziel_status).label : 'Kontaktiert';
  const zeilen = [`„${companyName}“ wird wieder als Lead geführt, Status „${ziel}“.`];

  if (v) {
    if (v.anfragen === 0 && v.projekte === 0 && v.angebote === 0) {
      zeilen.push('An diesem Kunden hängen keine Anfragen, Projekte oder Angebote.');
    } else {
      const angebote = zahlwort(v.angebote, 'Angebot', 'Angebote', 'kein Angebot');
      const haengt = [
        zahlwort(v.anfragen, 'Anfrage', 'Anfragen', 'keine Anfrage'),
        zahlwort(v.projekte, 'Projekt', 'Projekte', 'kein Projekt'),
        v.angebote_versandt > 0 ? `${angebote} (${v.angebote_versandt} davon versandt)` : angebote,
      ].join(', ');
      zeilen.push(`Nichts wird gelöscht: ${haengt} bleiben am Satz.`);
    }
    // Gebäude, Kontakte und Interaktionen wurden bis zum 29.10.2026 geholt,
    // getippt und nie gezeigt — drei von sieben Zahlen liefen nur ins
    // Protokoll. Sie stehen in einer zweiten Zeile und nicht in der ersten:
    // Anfragen, Projekte und Angebote entscheiden, Stammdaten und Verlauf
    // begleiten. Nullen bleiben hier weg, sonst stünde dreimal „kein" da,
    // wo es nichts zu bedenken gibt.
    const begleitend = [
      zahlwort(v.gebaeude, 'Gebäude', 'Gebäude', ''),
      zahlwort(v.kontakte, 'Kontakt', 'Kontakte', ''),
      zahlwort(v.interaktionen, 'Interaktion', 'Interaktionen', ''),
    ].filter(Boolean);
    if (begleitend.length > 0) {
      // „Auch 1 Gebäude bleibt", nicht „bleiben" — Einzahl nur, wenn es
      // wirklich ein einziges Stück ist.
      const einzahl = begleitend.length === 1 && begleitend[0].startsWith('1 ');
      zeilen.push(`Auch ${aufzaehlung(begleitend)} ${einzahl ? 'bleibt' : 'bleiben'} unverändert.`);
    }

    zeilen.push(`Die Kundennummer ${v.customer_number} bleibt.`);

    // Die Quote nur dort versprechen, wo sie auch eintritt: Das Dashboard
    // zählt „Status won UND activated_at gesetzt" (`total_converted` in
    // services/dashboard.py). Ein Kunde in „Angebot gesendet" stand dort nie
    // — ihm zu sagen, er zähle „dann nicht mehr in der Umwandlungsquote",
    // wäre eine Folge, die nicht eintritt (Gegenprüfung 29.10., Befund 8).
    if (v.zaehlt_als_umwandlung) {
      zeilen.push(
        'Das Aktivierungsdatum wird geleert — der Satz zählt dann nicht mehr in der Umwandlungsquote.',
      );
    } else if (v.aktivierung_wird_geleert) {
      zeilen.push(
        'Das Aktivierungsdatum wird geleert; die Umwandlung gilt damit als zurückgenommen.',
      );
    }
  }

  return zeilen;
}

/**
 * Rückweg Kunde → Lead, Gegenstück zu „Als Kunde anlegen" am Lead.
 *
 * In-App-Dialog, kein `window.confirm` (Hausregel). Die Fehlermeldung kommt
 * aus `ConfirmDialog` selbst, der `response.data.detail` der API zeigt —
 * bewusst nicht über `formatApiError`, dessen deutscher Ersatztext nie greift,
 * weil der Rückfall auf die englische Axios-Meldung nicht leer ist.
 */
export function ZurueckZumLeadDialog({
  customerId,
  companyName,
  open,
  onOpenChange,
}: ZurueckZumLeadDialogProps) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: vorschau, isPending: vorschauLaeuft, isError: vorschauFehler } = useQuery({
    queryKey: ['customers', customerId, 'rueckweg-vorschau'],
    queryFn: () => customersApi.rueckwegVorschau(customerId),
    enabled: open,
    // Beim nächsten Öffnen frisch zählen: In der Zwischenzeit kann ein
    // Angebot rausgegangen sein.
    staleTime: 0,
  });

  const mutation = useMutation({
    mutationFn: () => customersApi.zurueckZumLead(customerId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success(`„${companyName}“ ist wieder ein Lead`);
      navigate(`/leads/${customerId}`);
    },
  });

  const zeilen = rueckfrageZeilen(vorschau, companyName);

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Wieder als Lead führen?"
      description={
        <>
          {zeilen.map((zeile, i) => (
            <span key={i}>
              {i > 0 && <br />}
              {zeile}
            </span>
          ))}
          {vorschauLaeuft && (
            <span>
              <br />
              Wird gezählt, was am Kunden hängt.
            </span>
          )}
          {vorschauFehler && (
            <span>
              <br />
              Was am Kunden hängt, ließ sich nicht zählen. Der Rückweg geht trotzdem; gelöscht
              wird dabei nichts.
            </span>
          )}
        </>
      }
      confirmLabel="Wieder als Lead führen"
      // Nicht `danger`: Es wird nichts gelöscht. Ein roter Knopf hier hätte
      // genau das Signal, das Leute am 29.10. ins Löschen getrieben hat.
      tone="primary"
      // Solange gezählt wird, ist der Knopf gesperrt — niemand soll
      // bestätigen, bevor die Zahlen dastehen. Ein Fehler beim Zählen sperrt
      // dagegen nicht (siehe Hinweis oben).
      busy={vorschauLaeuft}
      onConfirm={() => mutation.mutateAsync()}
    />
  );
}
